import { useCallback, useEffect, useState } from "react";
import { Loader2, VideoOff, Wand2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const srcsIn = (html) =>
  [...String(html || "").matchAll(/<iframe[^>]+src="([^"]+)"/gi)]
    .map((m) => m[1].replace(/&amp;/g, "&"))
    .filter(Boolean);

/** Tells an editor, the moment they open an article, that a video in it no
 *  longer plays — and mends the address when the fix is knowable. */
export function KbBrokenEmbedWarning({ slug, bodyHtml, onRewrite }) {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);

  const check = useCallback(async (html) => {
    const srcs = srcsIn(html);
    if (!srcs.length) { setReport(null); return; }
    try {
      const { data } = await api.post("/kb/embeds/check", { srcs, slug });
      setReport(data);
    } catch { setReport(null); }
  }, [slug]);

  // Once, on open — probing on every keystroke would hammer the video hosts.
  useEffect(() => {
    if (bodyHtml === undefined || bodyHtml === null) return;
    check(bodyHtml);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyHtml === undefined]);

  const problems = report?.problems || [];
  if (hidden || !problems.length) return null;
  const fixable = problems.filter((p) => p.fix);

  const fix = () => {
    setBusy(true);
    const rewrites = Object.fromEntries(fixable.map((p) => [p.src, p.fix]));
    onRewrite?.(rewrites);
    toast.success(`${fixable.length} video address${fixable.length === 1 ? "" : "es"} corrected — save to keep it`);
    setBusy(false);
    setHidden(true);
  };

  return (
    <div data-testid="kb-broken-embed-warning"
      className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/40">
      <div className="flex items-start gap-2">
        <VideoOff className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">
            {problems.length === 1
              ? "A video in this article isn't playing"
              : `${problems.length} videos in this article aren't playing`}
          </div>
          <ul className="mt-1 space-y-0.5">
            {problems.slice(0, 5).map((p) => (
              <li key={p.src} className="truncate text-[11px] text-slate-600 dark:text-slate-300">
                <span className="font-medium">{p.name}</span>{" — "}{p.why}
                {p.fix && <span className="text-emerald-700 dark:text-emerald-400"> · can be corrected</span>}
              </li>
            ))}
            {problems.length > 5 && (
              <li className="text-[11px] text-slate-500">…and {problems.length - 5} more</li>
            )}
          </ul>
          {!!fixable.length && (
            <Button size="sm" className="mt-2 h-7 text-xs" onClick={fix} disabled={busy}
              data-testid="kb-broken-embed-fix">
              {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                : <Wand2 className="mr-1 h-3.5 w-3.5" />}
              Correct {fixable.length === problems.length ? "them" : `${fixable.length} of them`}
            </Button>
          )}
          {fixable.length < problems.length && (
            <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
              The rest need the original link back — check History, or the video's own
              sharing settings if the host is refusing us.
            </p>
          )}
        </div>
        <button type="button" onClick={() => setHidden(true)} aria-label="Dismiss"
          data-testid="kb-broken-embed-dismiss"
          className="rounded p-1 text-slate-400 transition-colors hover:text-slate-700">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
